/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (audit 6): the call error screen with iON texts. The look stays
 * Element Call's (ErrorView), only texts and buttons change: one plain
 * action, "Report a problem" instead of "contact your server admin", and
 * the error code folded under "Details" for support.
 */

import {
  type ComponentType,
  type FC,
  type ReactElement,
  type SVGAttributes,
  useCallback,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@vector-im/compound-web";
import { logger } from "matrix-js-sdk/lib/logger";

import {
  ConnectionLostError,
  E2EENotSupportedError,
  type ElementCallError,
  FailToGetOpenIdToken,
  FailToStartLivekitConnection,
  InsufficientCapacityError,
  LivekitConnectionError,
  MatrixRTCTransportMissingError,
  MembershipManagerError,
  NoMatrix2AuthorizationService,
  PeerConnectionTimeoutError,
  SFURoomCreationRestrictedError,
  StickyEventsRequiredError,
} from "../utils/errors";
import { ErrorView } from "../ErrorView";
import styles from "../ErrorView.module.css";
import { type Graylog, useGraylog } from "../logging/Graylog";
import { Config } from "../config/Config";
import { getLogsForReport } from "../settings/rageshake";
import {
  ACTION_TEXTS,
  CALL_ERROR_TEXTS,
  type IonCallErrorKind,
  pick,
} from "./callErrorTexts";

/** Which iON texts an error gets; SFU_ERROR is shared, so by type. */
export function ionCallErrorKind(error: ElementCallError): IonCallErrorKind {
  if (
    error instanceof MatrixRTCTransportMissingError ||
    error instanceof NoMatrix2AuthorizationService ||
    error instanceof StickyEventsRequiredError
  )
    return "serviceUnavailable";
  if (error instanceof ConnectionLostError) return "connectionLost";
  if (error instanceof MembershipManagerError) return "membership";
  if (error instanceof FailToStartLivekitConnection) return "startMedia";
  if (error instanceof InsufficientCapacityError) return "capacity";
  if (error instanceof SFURoomCreationRestrictedError) return "roomCreation";
  if (error instanceof PeerConnectionTimeoutError) return "networkBlocked";
  if (error instanceof LivekitConnectionError) return "livekitConnection";
  if (error instanceof FailToGetOpenIdToken) return "openId";
  if (error instanceof E2EENotSupportedError) return "browser";
  return "unknown";
}

const REPORT_LOG_CHARS = 8000;

/**
 * "Report a problem": iON has no rageshake server, so the report goes to
 * Graylog with the error and the end of the log.
 */
export async function reportCallProblem(
  graylog: Graylog,
  error: Error,
): Promise<void> {
  let logTail = "";
  try {
    const entries = await getLogsForReport();
    logTail = entries
      .map((entry) => entry.lines)
      .join("\n")
      .slice(-REPORT_LOG_CHARS);
  } catch (e) {
    logger.warn("No logs for the problem report", e);
  }
  graylog.logError("User reported a call problem", error, {
    errorCode: "code" in error ? error.code : undefined,
    errorCategory: "category" in error ? error.category : undefined,
    logTail,
  });
}

interface Props {
  error: ElementCallError;
  Icon: ComponentType<SVGAttributes<SVGElement>>;
  /** Rejoins the call: "Try again", "Reconnect", "Rejoin". */
  onRecover: () => void;
}

export const IonCallErrorPage: FC<Props> = ({
  error,
  Icon,
  onRecover,
}): ReactElement => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const graylog = useGraylog();
  const texts = CALL_ERROR_TEXTS[ionCallErrorKind(error)];
  const [reported, setReported] = useState(false);
  const [copied, setCopied] = useState(false);

  const onReport = useCallback(() => {
    setReported(true);
    void reportCallProblem(graylog, error);
  }, [graylog, error]);

  const onCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(
      () => setCopied(true),
      (e) => logger.warn("Could not copy the call link", e),
    );
  }, []);

  const reason =
    error instanceof LivekitConnectionError
      ? error.localisedMessageValues?.reason
      : undefined;

  return (
    <ErrorView Icon={Icon} title={pick(texts.title, language)}>
      <p>{pick(texts.description, language)}</p>
      {texts.action === "getIon" ? (
        <>
          <Button
            as="a"
            kind="primary"
            href={Config.get().call_links?.download_url ?? "https://i-on.chat"}
            target="_blank"
            rel="noopener noreferrer"
          >
            {pick(ACTION_TEXTS.getIon, language)}
          </Button>
          <Button kind="secondary" onClick={onCopyLink}>
            {pick(
              copied ? ACTION_TEXTS.linkCopied : ACTION_TEXTS.copyLink,
              language,
            )}
          </Button>
        </>
      ) : (
        <Button kind="primary" onClick={onRecover}>
          {pick(ACTION_TEXTS[texts.action], language)}
        </Button>
      )}
      {texts.report && (
        <Button kind="secondary" onClick={onReport} disabled={reported}>
          {pick(
            reported ? ACTION_TEXTS.reported : ACTION_TEXTS.report,
            language,
          )}
        </Button>
      )}
      <details className={styles.technicalDetails}>
        <summary className={styles.technicalDetailsSummary}>
          {pick(ACTION_TEXTS.details, language)}
        </summary>
        <pre className={styles.technicalDetailsPre}>
          {`${pick(ACTION_TEXTS.code, language)}: ${error.code}`}
          {reason ? `\n${pick(ACTION_TEXTS.reason, language)}: ${reason}` : ""}
        </pre>
      </details>
    </ErrorView>
  );
};
